/**
 * KBDeleteConfirm — delete confirmation dialog for KB documents.
 * US-007 Step 4: destructive confirmation with document name.
 * WCAG 2.1 AA: focus trap via Radix Dialog, aria-describedby on confirm button.
 */
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useKBDelete } from '@/hooks/useKBDocuments';

interface KBDeleteConfirmProps {
  docId: string | null;
  docTitle: string;
  isGlobal: boolean;
  onClose: () => void;
}

export function KBDeleteConfirm({
  docId,
  docTitle,
  isGlobal,
  onClose,
}: KBDeleteConfirmProps) {
  const { mutate: deleteDoc, isPending } = useKBDelete();
  const descId = 'kb-delete-description';

  const handleConfirm = () => {
    if (!docId) return;
    deleteDoc(docId, {
      onSuccess: onClose,
    });
  };

  return (
    <AlertDialog open={!!docId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Document</AlertDialogTitle>
          <AlertDialogDescription id={descId}>
            {isGlobal ? (
              'Global legislation documents cannot be deleted by client administrators.'
            ) : (
              <>
                Are you sure you want to delete{' '}
                <strong className="text-foreground">&ldquo;{docTitle}&rdquo;</strong>?
                {' '}This will remove the document and all its vector embeddings from the knowledge
                base. This action cannot be undone.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          {!isGlobal && (
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive' }))}
              onClick={handleConfirm}
              disabled={isPending}
              aria-describedby={descId}
              aria-busy={isPending}
            >
              {isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
